// ── participant-time: localized session-time rendering (Wave 9) ─────────────
// The participant dashboard shows session times in the PARTICIPANT'S timezone
// (their /auth/me profile timezone), falling back to the session's own timezone,
// then the browser's. Timestamps are full ISO (blueprint §1); we format with
// Intl.DateTimeFormat in the resolved zone. A missing/unparseable start renders
// honestly as "—" (never a fabricated time, never a silent drop).
//
// This is PRESENTATION ONLY — it never invents a date; it formats what the
// server-scoped session read returned. No raw passcode / URL / secret is ever
// rendered here (recordings carry presence-only, handled in the recordings
// section).

/** Resolve the zone to render a participant's session times in (fail-soft). */
export function resolveParticipantZone(
  profileTimezone: string | null | undefined,
  sessionTimezone: string | null | undefined
): string {
  const profile = (profileTimezone ?? "").trim();
  if (profile) return profile;
  const session = (sessionTimezone ?? "").trim();
  if (session) return session;
  // Browser default as the last resort; "UTC" if even that is unavailable.
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Format an ISO timestamp in the participant's zone as a localized,
 * timezone-stamped string (e.g. "Sun, Mar 8, 2026, 2:00 PM EST"). Returns "—"
 * for a missing/unparseable value (honest, never fabricated). Guards against an
 * invalid IANA zone by retrying in UTC so a bad profile zone never throws.
 */
export function formatLocalizedSessionTime(
  isoStart: string | null | undefined,
  zone: string
): string {
  const raw = (isoStart ?? "").trim();
  if (!raw) return "—";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";

  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };

  try {
    return new Intl.DateTimeFormat("en", { ...options, timeZone: zone }).format(date);
  } catch {
    // An invalid IANA zone (e.g. a typo'd profile zone) — never throw; show UTC.
    try {
      return new Intl.DateTimeFormat("en", { ...options, timeZone: "UTC" }).format(date);
    } catch {
      return "—";
    }
  }
}

/** Whether an ISO session start is in the future relative to `now` (default: now). */
export function isUpcoming(isoStart: string | null | undefined, now: Date = new Date()): boolean {
  const raw = (isoStart ?? "").trim();
  if (!raw) return false;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() >= now.getTime();
}

/** Sort key (epoch ms) for a session start; undated sorts last (Infinity). */
export function sessionStartSortKey(isoStart: string | null | undefined): number {
  const raw = (isoStart ?? "").trim();
  if (!raw) return Number.POSITIVE_INFINITY;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? Number.POSITIVE_INFINITY : date.getTime();
}
